
import { motion } from 'framer-motion';

// Shared container
const VisualContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full aspect-[4/3] bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-blue-100 shadow-sm overflow-hidden relative flex items-center justify-center p-3 sm:p-6 md:p-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f60a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f60a_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] pointer-events-none" />
        {children}
    </div>
);

// Component 1: Any Format You Need (File Type Selector)
export const FormatSelectorVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-4 sm:p-8 flex flex-col items-center justify-center relative overflow-hidden">

                {/* Central Hub (Deck) */}
                <div className="relative z-10 mb-4 sm:mb-8">
                    <div className="w-14 h-18 sm:w-20 sm:h-24 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-xl flex items-center justify-center text-white relative">
                        <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>

                        {/* Glow Behind */}
                        <div className="absolute inset-0 bg-blue-500 blur-xl opacity-40 -z-10" />
                    </div>
                </div>

                {/* Formats Grid */}
                <div className="grid grid-cols-4 gap-2 sm:gap-4 w-full max-w-[280px] sm:max-w-[320px]">
                    {[
                        { ext: 'CSV', color: 'bg-green-50 text-green-700 border-green-200' },
                        { ext: 'PDF', color: 'bg-red-50 text-red-700 border-red-200' },
                        { ext: 'DOC', color: 'bg-blue-50 text-blue-700 border-blue-200' },
                        { ext: 'PPTX', color: 'bg-orange-50 text-orange-700 border-orange-200' }
                    ].map((fmt, i) => (
                        <motion.div
                            key={i}
                            className={`aspect-square rounded-xl border ${fmt.color} flex flex-col items-center justify-center shadow-sm relative group cursor-default`}
                            initial={{ y: 20, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ delay: i * 0.1 + 0.2 }}
                            whileHover={{ y: -4, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
                        >
                            <span className="text-[10px] font-bold tracking-wider">{fmt.ext}</span>

                            {/* Download Icon on Hover */}
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 backdrop-blur-[1px] rounded-xl">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Connecting Lines (Mental Model) */}
                <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none opacity-20 -z-10">
                    <line x1="50%" y1="40%" x2="20%" y2="65%" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 4" />
                    <line x1="50%" y1="40%" x2="40%" y2="65%" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 4" />
                    <line x1="50%" y1="40%" x2="60%" y2="65%" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 4" />
                    <line x1="50%" y1="40%" x2="80%" y2="65%" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 4" />
                </svg>

            </div>
        </VisualContainer>
    );
};

// Component 2: High-Resolution Output (Crisp Quality)
export const ResolutionVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-0 flex flex-col items-center justify-center relative overflow-hidden group font-sans">

                {/* Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-b from-blue-50/20 to-transparent pointer-events-none" />

                {/* Main Quality Seal */}
                <motion.div
                    className="relative z-10 w-40 h-40 bg-white rounded-full shadow-[0_20px_50px_rgba(59,130,246,0.15)] flex items-center justify-center border border-blue-50 group-hover:scale-105 transition-transform duration-500"
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8 }}
                >
                    {/* Concentric Circles */}
                    <div className="absolute inset-0 m-1 rounded-full border border-blue-100" />
                    <div className="absolute inset-0 m-3 rounded-full border-2 border-dashed border-blue-200 opacity-30 animate-[spin_10s_linear_infinite]" />

                    {/* Center Icon */}
                    <div className="flex flex-col items-center gap-1">
                        <svg className="w-12 h-12 text-blue-600 drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1">Export Ready</span>
                    </div>

                    {/* Orbiting format badges */}
                    {[
                        { label: 'PDF', angle: 0 },
                        { label: 'DOC', angle: 120 },
                        { label: 'PPTX', angle: 240 }
                    ].map((item, i) => (
                        <motion.div
                            key={i}
                            className="absolute w-12 h-12 bg-white rounded-xl shadow-md border border-gray-100 flex flex-col items-center justify-center gap-0.5 z-20"
                            style={{
                                top: '50%',
                                left: '50%',
                                marginLeft: -24,
                                marginTop: -24,
                            }}
                            animate={{
                                x: [
                                    Math.cos(item.angle * (Math.PI / 180)) * 90,
                                    Math.cos((item.angle + 360) * (Math.PI / 180)) * 90
                                ],
                                y: [
                                    Math.sin(item.angle * (Math.PI / 180)) * 90,
                                    Math.sin((item.angle + 360) * (Math.PI / 180)) * 90
                                ]
                            }}
                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        >
                            <span className="text-[10px] font-extrabold text-gray-800">{item.label}</span>
                            <div className="w-1 h-1 rounded-full bg-green-500" />
                        </motion.div>
                    ))}
                </motion.div>

                {/* Bottom Text */}
                <motion.div
                    className="mt-12 text-center"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    <div className="text-sm font-bold text-gray-800">High Quality Downloads</div>
                    <div className="text-[10px] text-gray-500 mt-1">Pixel-perfect precision in every format</div>
                </motion.div>

            </div>
        </VisualContainer>
    );
};
